import type {
  HotTakeAnswer,
  HotTakesConfig,
  HotTakesConfigStatement,
  HotTakesResponse,
  HotTakesResult,
} from "@somethings-ui/core/hot-takes";

export interface TeenHotTakeStatement {
  id: string;
  text: string;
  authorAnswer: HotTakeAnswer;
}

export interface TeenHotTakesResponse {
  statementId: string;
  responderAnswer: HotTakeAnswer;
}

export interface TeenHotTakesConfig {
  statements: TeenHotTakeStatement[];
}

export interface TeenHotTakesResult {
  responses: TeenHotTakesResponse[];
  matchesCount?: number;
}

const isTeenAuthored = (
  statements: HotTakesConfigStatement[] | TeenHotTakeStatement[],
): statements is TeenHotTakeStatement[] =>
  statements.some((statement) => "authorAnswer" in statement);

const isTeenResponses = (
  responses: HotTakesResponse[] | TeenHotTakesResponse[],
): responses is TeenHotTakesResponse[] =>
  responses.length > 0 && "responderAnswer" in responses[0];

// Teen-initiated Hot Takes invert who's on the statements vs the responses
// (SOM-5008); map both shapes to mentorAnswer/teenAnswer for the renderers.
export const normalizeHotTakesShapes = (
  config: HotTakesConfig | TeenHotTakesConfig | undefined,
  result: HotTakesResult | TeenHotTakesResult | null | undefined,
): { statements: HotTakesConfigStatement[]; responses: HotTakesResponse[] } => {
  const statements = config?.statements ?? [];
  const responses = result?.responses ?? [];

  if (!isTeenAuthored(statements)) {
    return {
      statements,
      responses: isTeenResponses(responses) ? [] : responses,
    };
  }

  const responderById = new Map<string, HotTakeAnswer>(
    isTeenResponses(responses)
      ? responses.map((response) => [
          response.statementId,
          response.responderAnswer,
        ])
      : [],
  );

  return {
    statements: statements.map((statement) => ({
      id: statement.id,
      text: statement.text,
      mentorAnswer: responderById.get(statement.id) as HotTakeAnswer,
    })),
    responses: statements.map((statement) => ({
      statementId: statement.id,
      teenAnswer: statement.authorAnswer,
    })),
  };
};
